import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";

const STATUS_TONE = { New: "#3B82F6", Changed: "#F59E0B", "Action Required": "#EF4444" };
const IMPACT = { High: "#EF4444", Medium: "#F59E0B", Low: "#10B981" };
const SOURCE_TONE = { SEBI: "bg-blue-50 text-blue-600", AMFI: "bg-violet-50 text-violet-600", RBI: "bg-emerald-50 text-emerald-600", Exchanges: "bg-orange-50 text-orange-600" };
const STATUS_TABS = [["All", "All"], ["New", "What's New"], ["Changed", "What Changed"], ["Action Required", "Action Required"]];
const SOURCES = ["All", "SEBI", "AMFI", "RBI", "Exchanges"];

export default function Regulatory() {
  const nav = useNavigate();
  const [regs, setRegs] = useState([]);
  const [status, setStatus] = useState("All");
  const [source, setSource] = useState("All");
  const [open, setOpen] = useState(null);

  const load = useCallback(() => api.get("/regulatory").then((r) => setRegs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const view = useMemo(() => regs.filter((r) => (status === "All" || r.status === status) && (source === "All" || r.source === source)), [regs, status, source]);
  const actionCount = useMemo(() => regs.filter((r) => r.status === "Action Required").length, [regs]);
  const explain = (r) => nav("/app/ai", { state: { prompt: `Explain this ${r.source} regulatory update to me in simple language, and tell me exactly what I must do as a mutual fund distributor: "${r.title}". Deadline: ${r.deadline}. Keep it practical and short.` } });

  return (
    <div className="fade-up">
      <PageHeader title="Regulatory Hub" subtitle="SEBI · AMFI · RBI · Exchanges — decoded into what changed, why it matters and what to do." icon="ShieldCheck" color="#4F46E5" />

      <div className="rounded-2xl grad-blue-violet text-white p-5 mb-6 flex items-center gap-4" data-testid="reg-banner">
        <Icon name="Bell" size={22} />
        <div><div className="text-xs uppercase tracking-widest font-bold opacity-80">Needs your attention</div><p className="font-medium">{actionCount} update{actionCount !== 1 ? "s" : ""} require action — starting with the nomination deadline on 28 Aug 2026.</p></div>
      </div>

      <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar">
        {STATUS_TABS.map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`reg-status-${k.replace(/\s/g, "-").toLowerCase()}`}
            className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap border ${status === k ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>{l}</button>
        ))}
      </div>
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {SOURCES.map((s) => (
          <button key={s} onClick={() => setSource(s)} data-testid={`reg-source-${s.toLowerCase()}`}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border ${source === s ? "bg-gray-900 text-white border-transparent" : "bg-white border-gray-200 text-gray-500"}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-4" data-testid="reg-list">
        {view.map((r) => (
          <div key={r.id} className="card-mfx p-6 hover-lift" data-testid="reg-card">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`text-[11px] font-bold rounded-full px-2.5 py-0.5 ${SOURCE_TONE[r.source] || "bg-gray-100 text-gray-600"}`}>{r.source}</span>
              <span className="text-[11px] font-bold rounded-full px-2.5 py-0.5" style={{ background: `${STATUS_TONE[r.status]}18`, color: STATUS_TONE[r.status] }}>{r.status}</span>
              <span className="text-[11px] font-semibold rounded-full px-2.5 py-0.5" style={{ background: `${IMPACT[r.impact]}18`, color: IMPACT[r.impact] }}>Impact: {r.impact}</span>
              <span className="text-[11px] text-gray-400 flex items-center gap-1"><Icon name="CalendarClock" size={12} /> {r.deadline}</span>
              <span className="ml-auto text-[11px] text-gray-300">{r.ref}</span>
            </div>
            <h3 className="font-head font-semibold text-lg">{r.title}</h3>
            <div className="mt-3 rounded-xl bg-indigo-50 border border-indigo-100 p-4">
              <div className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-1">What changed</div>
              <p className="text-sm text-gray-700">{r.changed}</p>
            </div>
            {open === r.id && (
              <div className="mt-3 grid md:grid-cols-2 gap-3 fade-up">
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
                  <div className="text-xs font-bold text-amber-600 uppercase mb-1">Why it matters</div>
                  <p className="text-sm text-gray-700">{r.why}</p>
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                  <div className="text-xs font-bold text-emerald-600 uppercase mb-2">What you must do</div>
                  <ul className="space-y-1.5">{r.actions.map((a) => <li key={a} className="text-sm text-gray-700 flex gap-2"><Icon name="CheckCircle2" size={15} className="text-emerald-500 mt-0.5 shrink-0" />{a}</li>)}</ul>
                </div>
                {r.docs?.length > 0 && (
                  <div className="md:col-span-2 flex flex-wrap gap-2">
                    {r.docs.map((d) => <span key={d.name} className="text-xs bg-gray-100 text-gray-600 rounded-lg px-3 py-1.5 flex items-center gap-1.5"><Icon name="FileText" size={13} /> {d.name}</span>)}
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              <Btn variant="soft" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`reg-expand-${r.id}`}>{open === r.id ? "Hide details" : "What do I need to do?"}</Btn>
              <Btn variant="outline" onClick={() => explain(r)} data-testid={`reg-explain-${r.id}`}><Icon name="Sparkles" size={14} /> Explain in simple language</Btn>
            </div>
          </div>
        ))}
        {view.length === 0 && <div className="text-center text-gray-400 py-12"><Icon name="ShieldCheck" size={30} className="mx-auto mb-2 text-gray-300" />No updates for this filter.</div>}
      </div>
      <p className="text-xs text-gray-400 mt-6 flex items-center gap-2"><Icon name="Info" size={13} /> Regulatory summaries are illustrative samples. Always refer to official SEBI / AMFI / RBI sources for the authoritative text.</p>
    </div>
  );
}
